import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { chatAPI, studentsAPI, uploadAPI } from '../services/api';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { 
  FiSend, 
  FiLogOut, 
  FiUser, 
  FiMessageSquare,
  FiUsers,
  FiHelpCircle,
  FiTrash2,
  FiChevronRight,
  FiClock,
  FiMail,
  FiPhone,
  FiMapPin,
  FiBook,
  FiAward,
  FiCalendar,
  FiHome,
  FiFacebook,
  FiInfo,
  FiMic,
  FiMicOff,
  FiMoon,
  FiSun,
  FiDownload,
  FiMaximize2,
  FiFileText,
  FiCamera,
  FiX,
  FiFile,
  FiMenu,
  FiPaperclip,
  FiImage,
  FiCopy,
  FiCheck
} from 'react-icons/fi';
import { RiRobot2Fill } from 'react-icons/ri';
import DefaultAvatar from '../components/DefaultAvatar';
import { 
  exportAcademicRecordToPDF, 
  exportAcademicRecordToExcel, 
  exportScheduleToPDF, 
  exportScheduleToExcel,
  exportStudentInfoToPDF 
} from '../utils/exportUtils';
import './Dashboard.css';

// Helper function to get the correct image URL (handles both local and Supabase URLs)
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  // If URL already starts with http/https, it's a full URL (Supabase)
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  // Otherwise, it's a local path that needs the server prefix
  return `http://localhost:5000${imageUrl}`;
};

// Pure helper functions - defined outside component for stable references
const getFileIcon = (mimeType) => {
  if (mimeType?.startsWith('image/')) return <FiImage />;
  return <FiFileText />;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const formatTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatTimeTo12Hour = (time24) => {
  if (!time24) return 'TBA';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

const getYearLevelLabel = (level) => {
  const labels = ['', '1st Year', '2nd Year', '3rd Year', '4th Year'];
  return labels[level] || `Year ${level}`;
};

const getStatusBadgeClass = (status) => {
  const classes = {
    'Enrolled': 'badge-success',
    'Not Enrolled': 'badge-warning',
    'Graduated': 'badge-info',
    'Dropped': 'badge-error',
    'Leave of Absence': 'badge-warning',
    'Regular': 'badge-success',
    'Irregular': 'badge-warning',
    'Probation': 'badge-error',
    "Dean's List": 'badge-info',
    "President's List": 'badge-primary',
  };
  return classes[status] || 'badge-default';
};

// Copy button component for code blocks
const CodeCopyButton = ({ code }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button className="code-copy-btn" onClick={handleCopy} title="Copy code">
      {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
      <span>{copied ? 'Copied!' : 'Copy'}</span>
    </button>
  );
};

// Memoized markdown components to avoid recreating on every render
const markdownComponents = {
  code({ node, inline, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || '');
    const codeString = String(children).replace(/\n$/, '');
    return !inline && match ? (
      <div className="code-block-wrapper">
        <div className="code-block-header">
          <span className="code-block-lang">{match[1]}</span>
          <CodeCopyButton code={codeString} />
        </div>
        <SyntaxHighlighter
          style={oneDark}
          language={match[1]}
          PreTag="div"
          customStyle={{ borderRadius: '0 0 8px 8px', fontSize: '0.85rem', margin: '0' }}
          {...props}
        >
          {codeString}
        </SyntaxHighlighter>
      </div>
    ) : (
      <code className={`inline-code ${className || ''}`} {...props}>
        {children}
      </code>
    );
  }
};

// Memoized single message component to prevent re-rendering all messages on input change
const MessageItem = memo(({ message, user, onStudentClick, onZoomImage }) => {
  const ITEMS_PER_PAGE = 5;
  const [currentPage, setCurrentPage] = useState(0);

  const students = message.data && Array.isArray(message.data) ? message.data : [];
  const totalPages = Math.ceil(students.length / ITEMS_PER_PAGE);
  const paginatedStudents = students.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);
  const startIndex = currentPage * ITEMS_PER_PAGE;

  return (
  <div
    className={`message ${message.type === 'user' ? 'message-user' : 'message-bot'} ${message.isError ? 'message-error' : ''}`}
  >
    {message.type === 'bot' && (
      <div className="message-avatar">
        <div className="bot-avatar"><RiRobot2Fill /></div>
      </div>
    )}
    {message.type === 'user' && (
      <div className="message-avatar">
        <div className="user-avatar-chat">
          {user?.profileImage ? (
            <img 
              src={getImageUrl(user.profileImage)} 
              alt={`${user.firstName}'s profile`}
              className="chat-avatar-img"
            />
          ) : (
            <DefaultAvatar 
              firstName={user?.firstName} 
              lastName={user?.lastName} 
              size="sm"
            />
          )}
        </div>
      </div>
    )}
    <div className="message-content">
      <div className="message-bubble">
        {/* File attachment display */}
        {message.file && (
          <div className="message-file-attachment">
            {message.file.preview || (message.file.type?.startsWith('image/') && message.file.url) ? (
              <img 
                src={message.file.preview || message.file.url} 
                alt={message.file.name} 
                className="message-file-image"
                onClick={() => onZoomImage(message.file.preview || message.file.url)}
              />
            ) : (
              <div className="message-file-doc">
                <div className="message-file-doc-icon">{getFileIcon(message.file.type)}</div>
                <div className="message-file-doc-info">
                  <span className="message-file-doc-name">{message.file.name}</span>
                  {message.file.size && <span className="message-file-doc-size">{formatFileSize(message.file.size)}</span>}
                </div>
                {message.file.url && (
                  <a href={message.file.url} target="_blank" rel="noopener noreferrer" className="message-file-download">
                    <FiDownload />
                  </a>
                )}
              </div>
            )}
          </div>
        )}
        <ReactMarkdown
          remarkPlugins={[remarkMath]}
          rehypePlugins={[rehypeKatex]}
          components={markdownComponents}
        >
          {message.content}
        </ReactMarkdown>
        
        {/* Render student list if available */}
        {message.data && Array.isArray(message.data) && message.data.length > 0 && (
          <div className="student-results">
            {paginatedStudents.map((student, idx) => (
              <div 
                key={startIndex + idx} 
                className="student-card-mini"
                onClick={() => onStudentClick(student)}
              >
                <div className="student-card-with-photo">
                  <div 
                    className="student-photo-mini clickable-avatar"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (student.profileImage) onZoomImage(getImageUrl(student.profileImage));
                    }}
                    title={student.profileImage ? "Click to view full size" : ""}
                  >
                    {student.profileImage ? (
                      <img src={getImageUrl(student.profileImage)} alt={student.firstName} />
                    ) : (
                      <DefaultAvatar 
                        firstName={student.firstName} 
                        lastName={student.lastName} 
                        size="lg"
                      />
                    )}
                  </div>
                  <div className="student-card-info">
                    <div className="student-card-header">
                      <span className="student-id">{student.studentId}</span>
                      <span className={`badge ${getStatusBadgeClass(student.enrollmentStatus)}`}>
                        {student.enrollmentStatus}
                      </span>
                    </div>
                    <div className="student-name">{student.fullName}</div>
                    <div className="student-meta">
                      <span>{getYearLevelLabel(student.yearLevel)}</span>
                    </div>
                    {student.fbAccount && (
                      <div className="student-meta student-fb">
                        <FiFacebook size={12} />
                        <span>{student.fbAccount}</span>
                      </div>
                    )}
                    {(student.fatherName || student.motherMaidenName) && (
                      <div className="student-parents-info">
                        {student.fatherName && (
                          <div className="parent-info">
                            <span className="parent-label">Father:</span>
                            <span className="parent-name">{student.fatherName}</span>
                            {student.fatherFbAccount && (
                              <span className="parent-fb"><FiFacebook size={10} /> {student.fatherFbAccount}</span>
                            )}
                          </div>
                        )}
                        {student.motherMaidenName && (
                          <div className="parent-info">
                            <span className="parent-label">Mother:</span>
                            <span className="parent-name">{student.motherMaidenName}</span>
                            {student.motherFbAccount && (
                              <span className="parent-fb"><FiFacebook size={10} /> {student.motherFbAccount}</span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {totalPages > 1 && (
              <div className="student-pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                >
                  &#8592; Previous
                </button>
                <span className="pagination-info">
                  {startIndex + 1}–{Math.min(startIndex + ITEMS_PER_PAGE, students.length)} of {students.length}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage === totalPages - 1}
                >
                  Next &#8594;
                </button>
              </div>
            )}
          </div>
        )}

        {/* Render dedicated Academic Record Card */}
        {message.data && message.data.type === 'academicRecord' && message.data.academicRecord && (
          <div className="academic-record-card">
            <div className="academic-record-header">
              <div 
                className="student-detail-avatar-large clickable-avatar"
                onClick={() => message.data.profileImage && onZoomImage(getImageUrl(message.data.profileImage))}
                title={message.data.profileImage ? "Click to view full size" : ""}
              >
                {message.data.profileImage ? (
                  <img 
                    src={getImageUrl(message.data.profileImage)} 
                    alt={message.data.firstName}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="avatar-initials" style={{ display: message.data.profileImage ? 'none' : 'flex' }}>
                  {message.data.firstName?.[0]}{message.data.lastName?.[0]}
                </div>
                {message.data.profileImage && <div className="zoom-icon"><FiMaximize2 size={12} /></div>}
              </div>
              <div className="student-detail-info">
                <h4>{message.data.fullName}</h4>
                <span className="student-detail-id">{message.data.studentId}</span>
                <div className="student-meta">
                  <span>{message.data.program} • {getYearLevelLabel(message.data.yearLevel)}</span>
                </div>
              </div>
            </div>
            
            <div className="academic-record-body">
              <div className="gwa-display">
                <span className="gwa-label">General Weighted Average (GWA)</span>
                <span className="gwa-value">{message.data.academicRecord.gwa}</span>
                <span className="gwa-units">{message.data.academicRecord.totalUnits} units</span>
              </div>
              
              <div className="export-buttons-group">
                <button 
                  className="export-btn pdf-btn"
                  onClick={() => exportAcademicRecordToPDF(message.data, message.data.academicRecord)}
                  title="Download as PDF"
                >
                  <FiFile size={14} /> PDF
                </button>
                <button 
                  className="export-btn excel-btn"
                  onClick={() => exportAcademicRecordToExcel(message.data, message.data.academicRecord)}
                  title="Download as Excel"
                >
                  <FiFileText size={14} /> Excel
                </button>
              </div>
              
              <div className="grades-list">
                {message.data.academicRecord.grades.map((semesterData, idx) => (
                  <div key={idx} className="semester-grades">
                    <h6 className="semester-title">
                      {semesterData.semester === 1 ? '1st Semester' : semesterData.semester === 2 ? '2nd Semester' : 'Summer'}, S.Y. {semesterData.schoolYear} ({getYearLevelLabel(semesterData.yearLevel || semesterData.subjects[0]?.yearLevel)})
                    </h6>
                    <div className="grades-table">
                      {semesterData.subjects.map((subject, subIdx) => (
                        <div key={subIdx} className="grade-row subject-card">
                          <div className="grade-subject">
                            <span className="course-code">{subject.courseCode}</span>
                            <span className="subject-title">{subject.subjectTitle}</span>
                            <span className="units-display">({subject.units} units)</span>
                            {subject.preRequisite && (
                              <span className="prereq-tag">Pre-req: {subject.preRequisite}</span>
                            )}
                            {subject.isTransferred && (
                              <span className="transferred-tag" title={subject.previousSchool ? `From: ${subject.previousSchool}` : 'Transferred/Credited grade'}>
                                ↗ Transferred{subject.previousSchool ? ` from ${subject.previousSchool}` : ''}
                              </span>
                            )}
                          </div>
                          <div className="periods-grades">
                            {subject.periods && semesterData.semester !== 3 && (
                              <div className="period-grades-row">
                                <span className="period-grade">
                                  <strong>Prelim:</strong> {subject.periods.prelim?.grade !== null && subject.periods.prelim?.grade !== undefined ? parseFloat(subject.periods.prelim.grade).toFixed(2) : 'N/A'}
                                </span>
                                <span className="period-grade">
                                  <strong>Midterm:</strong> {subject.periods.midterm?.grade !== null && subject.periods.midterm?.grade !== undefined ? parseFloat(subject.periods.midterm.grade).toFixed(2) : 'N/A'}
                                </span>
                                <span className="period-grade">
                                  <strong>Pre-Final:</strong> {subject.periods.prefinal?.grade !== null && subject.periods.prefinal?.grade !== undefined ? parseFloat(subject.periods.prefinal.grade).toFixed(2) : 'N/A'}
                                </span>
                                <span className="period-grade">
                                  <strong>Final:</strong> {subject.periods.final?.grade !== null && subject.periods.final?.grade !== undefined ? parseFloat(subject.periods.final.grade).toFixed(2) : 'N/A'}
                                </span>
                              </div>
                            )}
                            {subject.periods && semesterData.semester === 3 && (
                              <div className="period-grades-row ojt-grade">
                                <span className="period-grade">
                                  <strong>OJT Grade:</strong> {subject.periods.ojt?.grade !== null && subject.periods.ojt?.grade !== undefined ? parseFloat(subject.periods.ojt.grade).toFixed(2) : 'N/A'}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="final-grade-display">
                            <span className="final-grade-label">Final Grade:</span>
                            <span className={`grade-value ${subject.finalGrade && parseFloat(subject.finalGrade) <= 1.5 ? 'excellent' : subject.finalGrade && parseFloat(subject.finalGrade) <= 2.0 ? 'good' : subject.finalGrade && parseFloat(subject.finalGrade) <= 3.0 ? 'average' : 'poor'}`}>
                              {subject.finalGrade ? subject.finalGrade : 'N/A'}
                            </span>
                            <span className={`grade-status ${subject.finalGrade ? (parseFloat(subject.finalGrade) <= 3.0 ? 'status-passed' : 'status-failed') : 'status-incomplete'}`}>
                              {subject.finalGrade ? (parseFloat(subject.finalGrade) <= 3.0 ? 'Passed' : 'Failed') : 'Incomplete'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Render Class Schedule Card */}
        {message.data && message.data.type === 'classSchedule' && message.data.schedule && (
          <div className="class-schedule-card">
            <div className="schedule-header">
              <div 
                className="student-detail-avatar-large clickable-avatar"
                onClick={() => message.data.profileImage && onZoomImage(getImageUrl(message.data.profileImage))}
                title={message.data.profileImage ? "Click to view full size" : ""}
              >
                {message.data.profileImage ? (
                  <img 
                    src={getImageUrl(message.data.profileImage)} 
                    alt={message.data.firstName}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="avatar-initials" style={{ display: message.data.profileImage ? 'none' : 'flex' }}>
                  {message.data.firstName?.[0]}{message.data.lastName?.[0]}
                </div>
                {message.data.profileImage && <div className="zoom-icon"><FiMaximize2 size={12} /></div>}
              </div>
              <div className="student-detail-info">
                <h4>{message.data.fullName}</h4>
                <span className="student-detail-id">{message.data.studentId}</span>
                <div className="student-meta">
                  <span>{message.data.program} • {getYearLevelLabel(message.data.yearLevel)} • {message.data.semester} Semester</span>
                </div>
              </div>
              <div className="export-buttons-group schedule-export">
                <button 
                  className="export-btn pdf-btn"
                  onClick={() => exportScheduleToPDF(message.data, message.data.schedule)}
                  title="Download Schedule as PDF"
                >
                  <FiFile size={14} /> PDF
                </button>
                <button 
                  className="export-btn excel-btn"
                  onClick={() => exportScheduleToExcel(message.data, message.data.schedule)}
                  title="Download Schedule as Excel"
                >
                  <FiFileText size={14} /> Excel
                </button>
              </div>
            </div>
            
            <div className="schedule-body">
              {message.data.dayLabel && (
                <div className="schedule-day-indicator">
                  <FiCalendar className="day-icon" />
                  <span>{message.data.dayLabel} ({message.data.targetDay})</span>
                </div>
              )}
              
              <div className="schedule-list">
                {Object.entries(message.data.schedule).map(([day, classes]) => (
                  <div key={day} className="day-schedule">
                    {!message.data.targetDay && (
                      <h6 className="day-title">
                        <FiCalendar className="day-icon" /> {day}
                      </h6>
                    )}
                    <div className="classes-list">
                      {classes.map((classItem, idx) => (
                        <div key={idx} className="class-item">
                          <div className="class-time">
                            <FiClock className="time-icon" />
                            <span>
                              {classItem.timeStart && classItem.timeEnd 
                                ? `${formatTimeTo12Hour(classItem.timeStart)} - ${formatTimeTo12Hour(classItem.timeEnd)}`
                                : 'TBA'}
                            </span>
                          </div>
                          <div className="class-details">
                            <span className="class-code">{classItem.courseCode}</span>
                            <span className="class-title">{classItem.subjectTitle}</span>
                            <div className="class-meta">
                              {classItem.section && (
                                <span className="class-section">
                                  Section {classItem.section}
                                </span>
                              )}
                              <span className="class-room">
                                <FiMapPin className="meta-icon" /> {classItem.room}
                              </span>
                              <span className="class-instructor">
                                <FiUser className="meta-icon" /> {classItem.instructor}
                              </span>
                              <span className="class-units">
                                ({classItem.units} units)
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Render single student details */}
        {message.data && !Array.isArray(message.data) && message.data.studentId && message.data.type !== 'academicRecord' && message.data.type !== 'subjectGrade' && message.data.type !== 'classSchedule' && (
          <div className="student-detail-card">
            <div className="student-detail-header">
              <div 
                className="student-detail-avatar-large clickable-avatar"
                onClick={() => message.data.profileImage && onZoomImage(getImageUrl(message.data.profileImage))}
                title={message.data.profileImage ? "Click to view full size" : ""}
              >
                {message.data.profileImage ? (
                  <img 
                    src={getImageUrl(message.data.profileImage)} 
                    alt={message.data.firstName}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="avatar-initials" style={{ display: message.data.profileImage ? 'none' : 'flex' }}>
                  {message.data.firstName?.[0]}{message.data.lastName?.[0]}
                </div>
                {message.data.profileImage && <div className="zoom-icon"><FiMaximize2 size={12} /></div>}
              </div>
              <div className="student-detail-info">
                <h4>{message.data.fullName}</h4>
                <span className="student-detail-id">{message.data.studentId}</span>
                <div className="student-status-badges">
                  <span className={`badge ${getStatusBadgeClass(message.data.enrollmentStatus)}`}>
                    {message.data.enrollmentStatus}
                  </span>
                  <span className={`badge ${getStatusBadgeClass(message.data.academicStatus)}`}>
                    {message.data.academicStatus}
                  </span>
                </div>
              </div>
              <div className="export-buttons-group student-info-export">
                <button 
                  className="export-btn pdf-btn"
                  onClick={() => exportStudentInfoToPDF(message.data)}
                  title="Download Student Info as PDF"
                >
                  <FiFile size={14} /> PDF
                </button>
              </div>
            </div>
            
            <div className="student-detail-body">
              {/* Student's Profile Section */}
              <div className="detail-section">
                <h5 className="detail-section-title">
                  <FiInfo /> Student's Profile
                </h5>
                <div className="detail-grid">
                  {message.data.studentType && (
                    <div className="detail-item">
                      <FiFileText className="detail-icon" />
                      <div>
                        <span className="detail-label">Student Type</span>
                        <span className="detail-value">{message.data.studentType}</span>
                      </div>
                    </div>
                  )}
                  <div className="detail-item">
                    <FiBook className="detail-icon" />
                    <div>
                      <span className="detail-label">Program</span>
                      <span className="detail-value">{message.data.program}</span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <FiUsers className="detail-icon" />
                    <div>
                      <span className="detail-label">Year Level</span>
                      <span className="detail-value">{getYearLevelLabel(message.data.yearLevel)}</span>
                    </div>
                  </div>
                  {message.data.section && (
                    <div className="detail-item">
                      <FiUsers className="detail-icon" />
                      <div>
                        <span className="detail-label">Section</span>
                        <span className="detail-value">{message.data.section}</span>
                      </div>
                    </div>
                  )}
                  {message.data.learnerReferenceNumber && (
                    <div className="detail-item">
                      <FiFileText className="detail-icon" />
                      <div>
                        <span className="detail-label">Learner's Reference No.</span>
                        <span className="detail-value">{message.data.learnerReferenceNumber}</span>
                      </div>
                    </div>
                  )}
                  {message.data.dateOfBirth && (
                    <div className="detail-item">
                      <FiCalendar className="detail-icon" />
                      <div>
                        <span className="detail-label">Date of Birth</span>
                        <span className="detail-value">
                          {message.data.dateOfBirth.includes('/') ? message.data.dateOfBirth : new Date(message.data.dateOfBirth).toLocaleDateString()}
                          {' '}
                          <span className="age-badge">
                            ({(() => {
                              const today = new Date();
                              let birth;
                              if (message.data.dateOfBirth.includes('/')) {
                                const [mm, dd, yyyy] = message.data.dateOfBirth.split('/');
                                birth = new Date(yyyy, mm - 1, dd);
                              } else {
                                birth = new Date(message.data.dateOfBirth);
                              }
                              let age = today.getFullYear() - birth.getFullYear();
                              const monthDiff = today.getMonth() - birth.getMonth();
                              if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
                                age--;
                              }
                              return age;
                            })()} years old)
                          </span>
                        </span>
                      </div>
                    </div>
                  )}
                  {message.data.birthplace && (
                    <div className="detail-item">
                      <FiMapPin className="detail-icon" />
                      <div>
                        <span className="detail-label">Birthplace</span>
                        <span className="detail-value">{message.data.birthplace}</span>
                      </div>
                    </div>
                  )}
                  {message.data.gender && (
                    <div className="detail-item">
                      <FiUser className="detail-icon" />
                      <div>
                        <span className="detail-label">Gender</span>
                        <span className="detail-value">{message.data.gender}</span>
                      </div>
                    </div>
                  )}
                  {message.data.contactNumber && (
                    <div className="detail-item">
                      <FiPhone className="detail-icon" />
                      <div>
                        <span className="detail-label">Contact Number</span>
                        <span className="detail-value">{message.data.contactNumber}</span>
                      </div>
                    </div>
                  )}
                  <div className="detail-item">
                    <FiMail className="detail-icon" />
                    <div>
                      <span className="detail-label">Email</span>
                      <span className="detail-value">{message.data.email}</span>
                    </div>
                  </div>
                  {message.data.civilStatus && (
                    <div className="detail-item">
                      <FiUser className="detail-icon" />
                      <div>
                        <span className="detail-label">Civil Status</span>
                        <span className="detail-value">{message.data.civilStatus}</span>
                      </div>
                    </div>
                  )}
                  {message.data.masbateCityAddress && (
                    <div className="detail-item full-width">
                      <FiMapPin className="detail-icon" />
                      <div>
                        <span className="detail-label">Masbate City Address</span>
                        <span className="detail-value">{message.data.masbateCityAddress}</span>
                      </div>
                    </div>
                  )}
                  {message.data.cityResidenceType && (
                    <div className="detail-item">
                      <FiHome className="detail-icon" />
                      <div>
                        <span className="detail-label">Type of City Residence</span>
                        <span className="detail-value">{message.data.cityResidenceType}</span>
                      </div>
                    </div>
                  )}
                  {message.data.fbAccount && (
                    <div className="detail-item">
                      <FiFacebook className="detail-icon" />
                      <div>
                        <span className="detail-label">Facebook</span>
                        <span className="detail-value">{message.data.fbAccount}</span>
                      </div>
                    </div>
                  )}
                  {message.data.guardianName && (
                    <div className="detail-item">
                      <FiUser className="detail-icon" />
                      <div>
                        <span className="detail-label">Guardian Name</span>
                        <span className="detail-value">{message.data.guardianName}</span>
                      </div>
                    </div>
                  )}
                  {message.data.guardianContact && (
                    <div className="detail-item">
                      <FiPhone className="detail-icon" />
                      <div>
                        <span className="detail-label">Guardian Contact</span>
                        <span className="detail-value">{message.data.guardianContact}</span>
                      </div>
                    </div>
                  )}
                  {message.data.guardianAddress && (
                    <div className="detail-item full-width">
                      <FiHome className="detail-icon" />
                      <div>
                        <span className="detail-label">Guardian Address</span>
                        <span className="detail-value">{message.data.guardianAddress}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Academic Information Section */}
              <div className="detail-section">
                <h5 className="detail-section-title">
                  <FiAward /> Academic Information
                </h5>
                <div className="detail-grid">
                  <div className="detail-item">
                    <FiBook className="detail-icon" />
                    <div>
                      <span className="detail-label">Total Units Earned</span>
                      <span className="detail-value">{message.data.totalUnitsEarned || 0}</span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <FiBook className="detail-icon" />
                    <div>
                      <span className="detail-label">Remaining Units</span>
                      <span className="detail-value">{148 - (message.data.totalUnitsEarned || 0)}</span>
                    </div>
                  </div>
                  {message.data.enrollmentDate && (
                    <div className="detail-item">
                      <FiCalendar className="detail-icon" />
                      <div>
                        <span className="detail-label">Enrollment Date</span>
                        <span className="detail-value">{new Date(message.data.enrollmentDate).toLocaleDateString()}</span>
                      </div>
                    </div>
                  )}
                  {message.data.expectedGraduationDate && (
                    <div className="detail-item">
                      <FiCalendar className="detail-icon" />
                      <div>
                        <span className="detail-label">Expected Graduation</span>
                        <span className="detail-value">{new Date(message.data.expectedGraduationDate).toLocaleDateString()}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Family Background Information Section */}
              {(message.data.fatherName || message.data.motherMaidenName) && (
                <div className="detail-section">
                  <h5 className="detail-section-title">
                    <FiUsers /> Family Background Information
                  </h5>
                  <div className="detail-grid">
                    {message.data.fatherName && (
                      <div className="detail-item">
                        <FiUser className="detail-icon" />
                        <div>
                          <span className="detail-label">Father's Name</span>
                          <span className="detail-value">{message.data.fatherName}</span>
                        </div>
                      </div>
                    )}
                    {message.data.fatherOccupation && (
                      <div className="detail-item">
                        <FiFileText className="detail-icon" />
                        <div>
                          <span className="detail-label">Father's Occupation</span>
                          <span className="detail-value">{message.data.fatherOccupation}</span>
                        </div>
                      </div>
                    )}
                    {message.data.fatherContactNumber && (
                      <div className="detail-item">
                        <FiPhone className="detail-icon" />
                        <div>
                          <span className="detail-label">Father's Contact</span>
                          <span className="detail-value">{message.data.fatherContactNumber}</span>
                        </div>
                      </div>
                    )}
                    {message.data.fatherFbAccount && (
                      <div className="detail-item">
                        <FiFacebook className="detail-icon" />
                        <div>
                          <span className="detail-label">Father's Facebook</span>
                          <span className="detail-value">{message.data.fatherFbAccount}</span>
                        </div>
                      </div>
                    )}
                    {message.data.fatherAddress && (
                      <div className="detail-item full-width">
                        <FiHome className="detail-icon" />
                        <div>
                          <span className="detail-label">Father's Address</span>
                          <span className="detail-value">{message.data.fatherAddress}</span>
                        </div>
                      </div>
                    )}
                    {message.data.motherMaidenName && (
                      <div className="detail-item">
                        <FiUser className="detail-icon" />
                        <div>
                          <span className="detail-label">Mother's Maiden Name</span>
                          <span className="detail-value">{message.data.motherMaidenName}</span>
                        </div>
                      </div>
                    )}
                    {message.data.motherOccupation && (
                      <div className="detail-item">
                        <FiFileText className="detail-icon" />
                        <div>
                          <span className="detail-label">Mother's Occupation</span>
                          <span className="detail-value">{message.data.motherOccupation}</span>
                        </div>
                      </div>
                    )}
                    {message.data.motherContactNumber && (
                      <div className="detail-item">
                        <FiPhone className="detail-icon" />
                        <div>
                          <span className="detail-label">Mother's Contact</span>
                          <span className="detail-value">{message.data.motherContactNumber}</span>
                        </div>
                      </div>
                    )}
                    {message.data.motherFbAccount && (
                      <div className="detail-item">
                        <FiFacebook className="detail-icon" />
                        <div>
                          <span className="detail-label">Mother's Facebook</span>
                          <span className="detail-value">{message.data.motherFbAccount}</span>
                        </div>
                      </div>
                    )}
                    {message.data.motherAddress && (
                      <div className="detail-item full-width">
                        <FiHome className="detail-icon" />
                        <div>
                          <span className="detail-label">Mother's Address</span>
                          <span className="detail-value">{message.data.motherAddress}</span>
                        </div>
                      </div>
                    )}
                    {message.data.numberOfSiblings && (
                      <div className="detail-item">
                        <FiUsers className="detail-icon" />
                        <div>
                          <span className="detail-label">No. of Siblings</span>
                          <span className="detail-value">{message.data.numberOfSiblings}</span>
                        </div>
                      </div>
                    )}
                    {message.data.birthOrder && (
                      <div className="detail-item">
                        <FiFileText className="detail-icon" />
                        <div>
                          <span className="detail-label">Birth Order</span>
                          <span className="detail-value">{message.data.birthOrder}</span>
                        </div>
                      </div>
                    )}
                    {message.data.studySupportPerson && (
                      <div className="detail-item">
                        <FiUser className="detail-icon" />
                        <div>
                          <span className="detail-label">Study Support</span>
                          <span className="detail-value">
                            {message.data.studySupportPerson}
                            {message.data.studySupportRelationship && ` (${message.data.studySupportRelationship})`}
                          </span>
                        </div>
                      </div>
                    )}
                    {message.data.studySupportContact && (
                      <div className="detail-item">
                        <FiPhone className="detail-icon" />
                        <div>
                          <span className="detail-label">Support Contact</span>
                          <span className="detail-value">{message.data.studySupportContact}</span>
                        </div>
                      </div>
                    )}
                    {message.data.parentType && (
                      <div className="detail-item">
                        <FiUsers className="detail-icon" />
                        <div>
                          <span className="detail-label">Family Type</span>
                          <span className="detail-value">{message.data.parentType}</span>
                        </div>
                      </div>
                    )}
                    {message.data.monthlyFamilyIncome && (
                      <div className="detail-item">
                        <FiFileText className="detail-icon" />
                        <div>
                          <span className="detail-label">Monthly Family Income</span>
                          <span className="detail-value">{message.data.monthlyFamilyIncome}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Educational Background Section */}
              {(message.data.tertiarySchool || message.data.seniorHighSchool || message.data.juniorHighSchool || message.data.elementarySchool) && (
                <div className="detail-section">
                  <h5 className="detail-section-title">
                    <FiBook /> Educational Background
                  </h5>
                  <div className="detail-grid">
                    {message.data.tertiarySchool && (
                      <div className="detail-item full-width">
                        <FiAward className="detail-icon" />
                        <div>
                          <span className="detail-label">Tertiary</span>
                          <span className="detail-value">
                            {message.data.tertiarySchool}
                            {message.data.tertiaryAddress && ` — ${message.data.tertiaryAddress}`}
                            {message.data.tertiaryCourse && ` (${message.data.tertiaryCourse})`}
                            {message.data.tertiaryDateGraduated && ` • Graduated: ${message.data.tertiaryDateGraduated}`}
                          </span>
                        </div>
                      </div>
                    )}
                    {message.data.seniorHighSchool && (
                      <div className="detail-item full-width">
                        <FiBook className="detail-icon" />
                        <div>
                          <span className="detail-label">Senior High School</span>
                          <span className="detail-value">
                            {message.data.seniorHighSchool}
                            {message.data.seniorHighAddress && ` — ${message.data.seniorHighAddress}`}
                            {message.data.seniorHighCourse && ` (${message.data.seniorHighCourse})`}
                            {message.data.seniorHighDateGraduated && ` • Graduated: ${message.data.seniorHighDateGraduated}`}
                          </span>
                        </div>
                      </div>
                    )}
                    {message.data.juniorHighSchool && (
                      <div className="detail-item full-width">
                        <FiBook className="detail-icon" />
                        <div>
                          <span className="detail-label">Junior High School</span>
                          <span className="detail-value">
                            {message.data.juniorHighSchool}
                            {message.data.juniorHighAddress && ` — ${message.data.juniorHighAddress}`}
                            {message.data.juniorHighDateGraduated && ` • Graduated: ${message.data.juniorHighDateGraduated}`}
                          </span>
                        </div>
                      </div>
                    )}
                    {message.data.elementarySchool && (
                      <div className="detail-item full-width">
                        <FiBook className="detail-icon" />
                        <div>
                          <span className="detail-label">Elementary</span>
                          <span className="detail-value">
                            {message.data.elementarySchool}
                            {message.data.elementaryAddress && ` — ${message.data.elementaryAddress}`}
                            {message.data.elementaryDateGraduated && ` • Graduated: ${message.data.elementaryDateGraduated}`}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <span className="message-time">
        <FiClock size={12} />
        {formatTime(message.timestamp)}
      </span>
    </div>
  </div>
  );
});

const Dashboard = () => {
  const { user, logout, refreshUser } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTypingWelcome, setIsTypingWelcome] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const profileInputRef = useRef(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const isRecognitionRunning = useRef(false);

  // Initialize Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onstart = () => {
        isRecognitionRunning.current = true;
        setIsListening(true);
      };

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInputMessage(transcript);
      };

      recognitionRef.current.onend = () => {
        isRecognitionRunning.current = false;
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        isRecognitionRunning.current = false;
        setIsListening(false);
      };
    }
  }, []);

  // Toggle voice recognition
  const toggleListening = useCallback(() => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser. Please use Chrome.');
      return;
    }

    // Use the ref to check actual running state, not React state
    if (isRecognitionRunning.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.log('Recognition stop error:', e.message);
      }
      // State will be updated by onend handler
    } else {
      try {
        recognitionRef.current.start();
        // State will be updated by onstart handler
      } catch (e) {
        console.log('Recognition start error:', e.message);
        isRecognitionRunning.current = false;
        setIsListening(false);
      }
    }
  }, []);

  // Handle student profile photo upload
  const handleProfilePhotoUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size too large. Maximum size is 5MB.');
      return;
    }

    setIsUploadingPhoto(true);
    setUploadError(null);

    try {
      // Use the appropriate upload API based on user type
      const uploadFn = user?.userType === 'student' 
        ? uploadAPI.uploadStudentProfileImage 
        : uploadAPI.uploadTeacherProfileImage;
      
      const response = await uploadFn(file);
      if (response.data.success) {
        // Refresh user data to get the new profile image
        await refreshUser();
        setShowPhotoModal(false);
      } else {
        setUploadError(response.data.message || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Profile upload error:', error);
      setUploadError(error.response?.data?.message || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploadingPhoto(false);
      // Reset the input
      if (profileInputRef.current) {
        profileInputRef.current.value = '';
      }
    }
  }, [refreshUser, user?.userType]);

  // Load initial data
  useEffect(() => {
    loadSuggestions();
    loadStats();
    
    // Check globally saved chat history from backend
    const fetchHistory = async () => {
      try {
        const response = await chatAPI.getHistory();
        if (response.data && response.data.data && response.data.data.length > 0) {
          setMessages(response.data.data);
          setIsTypingWelcome(false);
          return;
        }
      } catch (err) {
        console.error('Failed to parse saved chat history from server', err);
      }
      addWelcomeMessage();
    };
    
    fetchHistory();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save chat history whenever messages change (debounced to reduce Firestore writes)
  useEffect(() => {
    if (messages.length === 0) return;
    const timeoutId = setTimeout(() => {
      // Sanitize messages before saving - remove blob: URLs that won't persist
      const sanitized = messages.map(msg => {
        if (msg.file && msg.file.preview && msg.file.preview.startsWith('blob:')) {
          const { preview, ...fileWithoutPreview } = msg.file;
          return { ...msg, file: fileWithoutPreview };
        }
        return msg;
      });
      chatAPI.saveHistory(sanitized).catch(console.error);
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [messages]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const clearChat = async () => {
    if (window.confirm('Are you sure you want to clear this conversation? All uploaded files will also be deleted.')) {
      try {
        await chatAPI.clearHistory();
      } catch (e) {
        console.error('Failed to clear history from server', e);
      }
      setMessages([]);
      handleRemoveFile();
      addWelcomeMessage();
    }
  };

  const addWelcomeMessage = () => {
    // Show typing indicator first
    setIsTypingWelcome(true);
    
    // After 3 seconds, show the welcome message with animation
    setTimeout(() => {
      setIsTypingWelcome(false);
      
      let welcomeContent;
      if (user?.userType === 'student') {
        welcomeContent = `Good day, **${user?.firstName || 'there'}**! Welcome to the CCS Student Information System. I can help you view your grades, academic record, and student information. What would you like to know?`;
      } else {
        let displayName = user?.firstName ? 'Prof. ' + user.firstName : 'Professor';
        
        if (user?.role === 'department_head' || (user?.position && user.position.toLowerCase() === 'dean')) {
          displayName = 'Engr. Violeta Monticalvo, Dean';
        } else if (user?.firstName?.toLowerCase().includes('violeta') || user?.lastName?.toLowerCase().includes('monticalvo')) {
          displayName = 'Engr. Violeta Monticalvo, Dean';
        }

        welcomeContent = `Good day, **${displayName}**! Welcome to the CCS Student Information System. How can I assist you today?`;
      }
      
      const welcomeMessage = {
        id: 'welcome',
        type: 'bot',
        content: welcomeContent,
        timestamp: new Date().toISOString(),
      };
      setMessages([welcomeMessage]);
    }, 3000);
  };

  const loadSuggestions = async () => {
    try {
      const response = await chatAPI.getSuggestions();
      if (response.data.success) {
        setSuggestions(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    }
  };

  const loadStats = async () => {
    // Only load stats for non-students (teachers/admins)
    if (user?.userType === 'student') return;
    
    try {
      const response = await studentsAPI.getStats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const ALLOWED_FILE_TYPES = [
    'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp',
    'application/pdf',
    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain', 'text/csv'
  ];
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      alert('Unsupported file type. Please upload an image, PDF, Word, Excel, PowerPoint, text, or CSV file.');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      alert('File is too large. Maximum size is 20MB.');
      e.target.value = '';
      return;
    }

    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };



  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    // Allow sending with either text or file
    if ((!inputMessage.trim() && !selectedFile) || isLoading) return;

    // If there's a file, handle file upload
    if (selectedFile) {
      const userMessage = {
        id: Date.now(),
        type: 'user',
        content: inputMessage.trim() || `Uploaded: ${selectedFile.name}`,
        timestamp: new Date().toISOString(),
        file: {
          name: selectedFile.name,
          size: selectedFile.size,
          type: selectedFile.type,
          preview: selectedFile.type.startsWith('image/') ? URL.createObjectURL(selectedFile) : null
        }
      };

      setMessages(prev => [...prev, userMessage]);
      setInputMessage('');
      const fileToUpload = selectedFile;
      handleRemoveFile();
      setIsLoading(true);
      setIsUploadingFile(true);

      const startTime = Date.now();

      try {
        const response = await chatAPI.sendFileMessage(fileToUpload, inputMessage.trim());
        
        const botMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: response.data.message,
          timestamp: response.data.timestamp || new Date().toISOString(),
          aiPowered: true,
          file: response.data.file ? {
            name: response.data.file.fileName,
            url: response.data.file.url,
            type: response.data.file.mimeType,
            size: response.data.file.size
          } : null
        };

        const elapsedTime = Date.now() - startTime;
        const remainingDelay = Math.max(0, 3000 - elapsedTime);

        setTimeout(() => {
          setMessages(prev => {
            // Update the user message's file with the Supabase URL so it persists
            const updated = prev.map(msg => {
              if (msg.id === userMessage.id && msg.file && response.data.file?.url) {
                return { ...msg, file: { ...msg.file, url: response.data.file.url, preview: undefined } };
              }
              return msg;
            });
            return [...updated, botMessage];
          });
          setIsLoading(false);
          setIsUploadingFile(false);
          inputRef.current?.focus();
        }, remainingDelay);
      } catch (error) {
        const errorMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: 'Sorry, I encountered an error processing your file. Please try again.',
          isError: true,
          timestamp: new Date().toISOString(),
        };
        
        const elapsedTime = Date.now() - startTime;
        const remainingDelay = Math.max(0, 3000 - elapsedTime);
        
        setTimeout(() => {
          setMessages(prev => [...prev, errorMessage]);
          setIsLoading(false);
          setIsUploadingFile(false);
          inputRef.current?.focus();
        }, remainingDelay);
      }
      return;
    }

    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setSelectedStudent(null);

    const startTime = Date.now();

    try {
      const response = await chatAPI.sendMessage(inputMessage);
      
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: response.data.message,
        data: response.data.data,
        queryType: response.data.queryType,
        resultsCount: response.data.resultsCount,
        timestamp: response.data.timestamp,
        aiPowered: response.data.aiPowered || false,
      };

      // Calculate remaining time to reach 3 seconds minimum
      const elapsedTime = Date.now() - startTime;
      const remainingDelay = Math.max(0, 3000 - elapsedTime);

      // Wait for remaining time before showing response
      setTimeout(() => {
        setMessages(prev => [...prev, botMessage]);
        setIsLoading(false);
        inputRef.current?.focus();
      }, remainingDelay);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        isError: true,
        timestamp: new Date().toISOString(),
      };
      
      // Also apply delay for error messages
      const elapsedTime = Date.now() - startTime;
      const remainingDelay = Math.max(0, 3000 - elapsedTime);
      
      setTimeout(() => {
        setMessages(prev => [...prev, errorMessage]);
        setIsLoading(false);
        inputRef.current?.focus();
      }, remainingDelay);
    }
  };

  const handleSuggestionClick = useCallback((query) => {
    setInputMessage(query);
    inputRef.current?.focus();
  }, []);

  const handleStudentClick = useCallback((student) => {
    setSelectedStudent(student);
  }, []);

  // Memoize blob URL to avoid creating new blob on every render
  const filePreviewUrl = useMemo(() => {
    if (selectedFile && selectedFile.type.startsWith('image/')) {
      return URL.createObjectURL(selectedFile);
    }
    return null;
  }, [selectedFile]);

  return (
    <div className="dashboard">
      {/* Sidebar Overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <img src="/logo.png" alt="CCS ChatBot Logo" className="sidebar-logo" />
            <div className="logo-text">
              <span className="logo-title">CCS ChatBot</span>
              <span className="logo-subtitle">
                {user?.userType === 'student' ? 'Student Portal' : '4th Year Student Information'}
              </span>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          {/* Show stats only for teachers */}
          {user?.userType !== 'student' && (
            <div className="nav-section">
              <span className="nav-section-title">4th Year Stats</span>
              {stats && (
                <div className="stats-grid">
                  <div className="stat-card">
                    <FiUsers className="stat-icon" />
                    <div className="stat-info">
                      <span className="stat-value">{stats.totalStudents}</span>
                      <span className="stat-label">4th Year Students</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Show student info for students */}
          {user?.userType === 'student' && (
            <div className="nav-section">
              <span className="nav-section-title">Your Information</span>
              <div className="student-info-card">
                <div className="student-info-item">
                  <FiUser className="info-icon" />
                  <span className="info-label">Student ID</span>
                  <span className="info-value">{user?.studentId}</span>
                </div>
                <div className="student-info-item">
                  <FiBook className="info-icon" />
                  <span className="info-label">Year Level</span>
                  <span className="info-value">{user?.yearLevel}</span>
                </div>
                {user?.section && (
                  <div className="student-info-item">
                    <FiUsers className="info-icon" />
                    <span className="info-label">Section</span>
                    <span className="info-value">{user?.section}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="nav-section">
            <span className="nav-section-title">Quick Queries</span>
            {suggestions.slice(0, 2).map((category, idx) => (
              <div key={idx} className="suggestion-group">
                <span className="suggestion-category">{category.category}</span>
                {category.queries.map((query, qIdx) => (
                  <button
                    key={qIdx}
                    className="suggestion-btn"
                    onClick={() => handleSuggestionClick(query)}
                  >
                    <FiChevronRight />
                    <span>{query}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar-wrapper">
              <div className="user-avatar">
                {user?.profileImage ? (
                  <img 
                    src={getImageUrl(user.profileImage)} 
                    alt={`${user.firstName}'s profile`}
                    className="user-avatar-img"
                  />
                ) : (
                  <DefaultAvatar 
                    firstName={user?.firstName} 
                    lastName={user?.lastName} 
                    size="md"
                  />
                )}
              </div>
              {(user?.userType === 'student' || user?.userType === 'teacher') && (
                <button 
                  className="change-photo-btn"
                  onClick={() => setShowPhotoModal(true)}
                  title="Change profile picture"
                >
                  <FiCamera />
                </button>
              )}
            </div>
            <div className="user-details">
              <span className="user-name">{user?.fullName || `${user?.firstName} ${user?.lastName}`}</span>
              <span className="user-role">
                {user?.userType === 'student' ? 'Student' : (user?.position || user?.role)}
              </span>
            </div>
          </div>
          <button className="logout-btn" onClick={logout}>
            <FiLogOut />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {/* Chat Container */}
        <div className="chat-container">
          <div className="chat-header">
            <button className="mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
              <FiMenu />
            </button>
            <div className="chat-header-info">
              <FiMessageSquare className="chat-icon" />
              <div>
                <h2>{user?.userType === 'student' ? 'CCS Student ChatBot' : 'CCS 4th Year ChatBot'}</h2>
                <p>{user?.userType === 'student' ? 'Ask about your grades and academic information' : 'Ask me anything about 4th Year CCS students'}</p>
              </div>
            </div>
            <div className="chat-header-actions">
              <button
                className={`btn-action ${isDarkMode ? 'active' : ''}`}
                onClick={toggleDarkMode}
                title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
              >
                {isDarkMode ? <FiSun /> : <FiMoon />}
              </button>
              <button className="btn-action" onClick={clearChat} title="Clear Chat">
                <FiTrash2 />
              </button>
              <button className="btn-action" title="Help">
                <FiHelpCircle />
              </button>
            </div>
          </div>

          <div className="chat-messages">
            {/* Welcome typing indicator */}
            {isTypingWelcome && (
              <div className="message message-bot">
                <div className="message-avatar">
                  <div className="bot-avatar"><RiRobot2Fill /></div>
                </div>
                <div className="message-content">
                  <div className="message-bubble typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            {messages.map((message) => (
              <MessageItem
                key={message.id}
                message={message}
                user={user}
                onStudentClick={handleStudentClick}
                onZoomImage={setZoomedImage}
              />
            ))}

            {isLoading && (
              <div className="message message-bot">
                <div className="message-avatar">
                  <div className="bot-avatar"><RiRobot2Fill /></div>
                </div>
                <div className="message-content">
                  <div className="message-bubble typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <form className="chat-input-container" onSubmit={handleSendMessage}>
            {selectedFile && (
              <div className="file-preview-bar">
                <div className="file-preview-info">
                  {selectedFile.type.startsWith('image/') ? (
                    <img src={filePreviewUrl} alt="Preview" className="file-preview-thumb" />
                  ) : (
                    <div className="file-preview-icon">{getFileIcon(selectedFile.type)}</div>
                  )}
                  <div className="file-preview-details">
                    <span className="file-preview-name">{selectedFile.name}</span>
                    <span className="file-preview-size">{formatFileSize(selectedFile.size)}</span>
                  </div>
                </div>
                <button type="button" className="file-preview-remove" onClick={handleRemoveFile}>
                  <FiX />
                </button>
              </div>
            )}
            <div className="chat-input-wrapper">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <button 
                type="button" 
                className="attach-btn"
                onClick={() => fileInputRef.current?.click()}
                title="Attach file"
                disabled={isLoading}
              >
                <FiPaperclip />
              </button>
              <button 
                type="button" 
                className={`voice-btn ${isListening ? 'listening' : ''}`}
                onClick={toggleListening}
                title={isListening ? 'Stop listening' : 'Voice input'}
              >
                {isListening ? <FiMicOff /> : <FiMic />}
              </button>
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder={isUploadingFile ? "Analyzing file..." : isListening ? "Listening... speak now" : selectedFile ? "Add a message about this file (optional)..." : "Ask about students... (e.g., 'Show first year students')"}
                disabled={isLoading}
                className={`chat-input ${isListening ? 'listening-input' : ''}`}
              />
              <button 
                type="submit" 
                className="send-btn"
                disabled={(!inputMessage.trim() && !selectedFile) || isLoading}
              >
                <FiSend />
              </button>
            </div>
            <div className="input-hint">
              Press Enter to send • 🎤 Click mic for voice input • 📎 Attach files • Try: "Find Juan Dela Cruz"
            </div>
          </form>
        </div>
      </main>

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div className="image-zoom-modal" onClick={() => setZoomedImage(null)}>
          <div className="image-zoom-content">
            <button className="zoom-close-btn" onClick={() => setZoomedImage(null)}>×</button>
            <img src={zoomedImage} alt="Student profile" />
          </div>
        </div>
      )}

      {/* Student Detail Sidebar */}
      {selectedStudent && (
        <aside className="detail-sidebar">
          <div className="detail-sidebar-header">
            <h3>Student Details</h3>
            <button 
              className="close-btn"
              onClick={() => setSelectedStudent(null)}
            >
              ×
            </button>
          </div>
          <div className="detail-sidebar-content">
            <div className="student-profile">
              <div className="profile-avatar">
                {selectedStudent.profileImage ? (
                  <img 
                    src={getImageUrl(selectedStudent.profileImage)} 
                    alt={selectedStudent.fullName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                  />
                ) : (
                  selectedStudent.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)
                )}
              </div>
              <h4>{selectedStudent.fullName}</h4>
              <span className="profile-id">{selectedStudent.studentId}</span>
              <div className="profile-badges">
                <span className={`badge ${getStatusBadgeClass(selectedStudent.enrollmentStatus)}`}>
                  {selectedStudent.enrollmentStatus}
                </span>
                <span className={`badge ${getStatusBadgeClass(selectedStudent.academicStatus)}`}>
                  {selectedStudent.academicStatus}
                </span>
              </div>
            </div>
            
            <div className="profile-details">
              <div className="profile-detail-item">
                <span className="detail-icon"><FiBook /></span>
                <div>
                  <span className="label">Program</span>
                  <span className="value">{selectedStudent.program || 'BS Computer Science'}</span>
                </div>
              </div>
              <div className="profile-detail-item">
                <span className="detail-icon"><FiUsers /></span>
                <div>
                  <span className="label">Year Level</span>
                  <span className="value">{getYearLevelLabel(selectedStudent.yearLevel)}</span>
                </div>
              </div>
              <div className="profile-detail-item">
                <span className="detail-icon"><FiMail /></span>
                <div>
                  <span className="label">Email</span>
                  <span className="value">{selectedStudent.email}</span>
                </div>
              </div>
            </div>
          </div>
        </aside>
      )}

      {/* Profile Photo Upload Modal */}
      {showPhotoModal && (
        <div className="modal-overlay" onClick={() => !isUploadingPhoto && setShowPhotoModal(false)}>
          <div className="photo-upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Change Profile Picture</h3>
              <button 
                className="modal-close-btn"
                onClick={() => !isUploadingPhoto && setShowPhotoModal(false)}
                disabled={isUploadingPhoto}
              >
                <FiX />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="current-photo">
                {user?.profileImage ? (
                  <img 
                    src={getImageUrl(user.profileImage)} 
                    alt="Current profile"
                    className="current-photo-img"
                  />
                ) : (
                  <div className="no-photo">
                    <DefaultAvatar 
                      firstName={user?.firstName} 
                      lastName={user?.lastName} 
                      size="xxl"
                    />
                  </div>
                )}
              </div>

              {uploadError && (
                <div className="upload-error">
                  <FiX className="error-icon" />
                  <span>{uploadError}</span>
                </div>
              )}

              <div className="upload-instructions">
                <p>Upload a new profile picture</p>
                <span className="upload-hint">JPEG, PNG, GIF, or WebP. Max 5MB.</span>
              </div>

              <input
                ref={profileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleProfilePhotoUpload}
                style={{ display: 'none' }}
                id="profile-photo-input"
              />

              <label 
                htmlFor="profile-photo-input"
                className={`upload-btn ${isUploadingPhoto ? 'uploading' : ''}`}
              >
                {isUploadingPhoto ? (
                  <>
                    <div className="spinner-small"></div>
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <FiCamera />
                    <span>Choose Photo</span>
                  </>
                )}
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
